/**
 * Advanced-list MATERIALIZED membership API contracts (plan 2026-06-09-001).
 *
 * Two admin-only, DB-sourced surfaces (no live Blackbaud call):
 *   - U8 GET  /v1/schools/:id/lists/:list_id/snapshot   — per-list display data
 *   - U9 GET  /v1/schools/:id/people/:person_id/lists    — reverse index
 *
 * Re-exports the canonical entity schemas from
 * `schemas/entities/list-membership` so handlers/use-cases reference one type.
 */

import { z } from 'zod'
import {
  ListMembershipSnapshotSchema,
  ListMembershipPreviewMemberSchema,
  ListMembershipColumnSchema,
  ListMembershipDisplayStateSchema,
  PersonListMembershipSchema,
  type ListMembershipSnapshot,
  type ListMembershipPreviewMember,
  type ListMembershipColumn,
  type ListMembershipDisplayState,
  type PersonListMembership
} from '../../schemas/entities/list-membership'

export {
  ListMembershipSnapshotSchema,
  ListMembershipPreviewMemberSchema,
  ListMembershipColumnSchema,
  ListMembershipDisplayStateSchema,
  PersonListMembershipSchema,
  type ListMembershipSnapshot,
  type ListMembershipPreviewMember,
  type ListMembershipColumn,
  type ListMembershipDisplayState,
  type PersonListMembership
}

// ───────────────────────────────────────────────────────────────────────────
// U8 — snapshot display
// ───────────────────────────────────────────────────────────────────────────

export const ListMembershipSnapshotResponseSchema = z.object({
  data: ListMembershipSnapshotSchema,
  message: z.string()
})

export type ListMembershipSnapshotResponse = z.infer<typeof ListMembershipSnapshotResponseSchema>

// ───────────────────────────────────────────────────────────────────────────
// U9 — reverse index ("which lists is this person on?")
// ───────────────────────────────────────────────────────────────────────────

export const PersonListsResponseDataSchema = z.object({
  lists: z.array(PersonListMembershipSchema),
  /**
   * Lower-bound signal (KTD2): AMBIGUOUS (shared-email) + name-only memberships
   * are excluded by construction, so a person may be on more lists than shown.
   * `excludedCandidateCount` counts the materialized rows that pointed at this
   * person's join value(s) but couldn't be exactly matched; `incomplete` is true
   * when that count is > 0 so "on 0 lists" never reads as definitive absence.
   */
  excludedCandidateCount: z.number().min(0),
  incomplete: z.boolean()
})

export type PersonListsResponseData = z.infer<typeof PersonListsResponseDataSchema>

export const PersonListsResponseSchema = z.object({
  data: PersonListsResponseDataSchema,
  message: z.string()
})

export type PersonListsResponse = z.infer<typeof PersonListsResponseSchema>
